import * as React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import classnames from 'classnames';

import * as PublicStore from '../../../app/store/PublicStore';

import { Loading, TaskContent } from '../../../youwell-common/components';
import { getContentTranslations, AUTOSAVE_TIMEOUT_INTERVAL, styles as defaultStyles } from '../../../common/components/VimoTaskContent';
import Auth from '../../../youwell-common/Auth';
import { getItemFromProgram } from '../../../youwell-common/taskContentUtils';
import { getPublicProgramContentLink } from '../../../common/taskUtils';
import { getSpecificTaskResponses, getUpdatedResponsesForTask } from '../../../youwell-common/responseUtils';

const styles = theme => ({
    ...defaultStyles(theme),
});

const useStyles = makeStyles(theme => styles(theme), { name: 'PublicTaskContent' });

const PublicTaskContent = ({
    forwardedRef, program, task, moduleId,
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const saveTimer = React.useRef();

    const translate = useSelector(state => getTranslate(state.localize));
    const allTaskResponses = useSelector(state => state.publicStore.responses);
    const patientProgramElementId = useSelector(state => state.publicStore.patientProgramElementId);
    const completedTasks = useSelector(state => state.publicStore.completedTasks);
    const [changeId, setChangeId] = React.useState(null);
    const [showSavingId, setShowSavingId] = React.useState(false);

    const responsesForCurrentTask = allTaskResponses && allTaskResponses[patientProgramElementId]
        && allTaskResponses[patientProgramElementId][task.id];
    const formId = responsesForCurrentTask?.formId;

    const updateResponse = (responses) => {
        const responseData = {
            programId: program.id,
            moduleId,
            taskId: task.id,
            taskVersion: task.version,
            patientProgramElementId,
            formId,
            responses,
        };

        setChangeId(Date.now());
        dispatch(PublicStore.actionCreators.updateResponse(responseData));
    };

    const saveResponses = React.useCallback((taskResponses, callback, showIsSaving) => {
        const responseData = changeId && getUpdatedResponsesForTask(taskResponses, patientProgramElementId, task.id, task.version, formId);

        if (responseData) {
            if (showIsSaving) {
                setShowSavingId(changeId);
            }
            dispatch(PublicStore.actionCreators.saveResponse(responseData, {
                background: true,
                callback: () => {
                    setChangeId(prevId => (prevId === changeId ? null : prevId));
                    setShowSavingId(prevId => (prevId === changeId ? null : prevId));

                    if (callback) {
                        callback();
                    }
                },
            }));
        } else if (callback) {
            callback();
        }
    }, [changeId, dispatch, formId, patientProgramElementId, task.id, task.version]);

    const saveChanges = (callback) => {
        if (responsesForCurrentTask) {
            if (Auth.isUserAuthenticated()) {
                clearTimeout(saveTimer.current);
                saveResponses(responsesForCurrentTask, callback, true);
            } else {
                const responseData = {
                    ...getUpdatedResponsesForTask(responsesForCurrentTask, null, task.id, task.version, null),
                    programId: program.id,
                    moduleId,
                    completedTasks: completedTasks ? Object.keys(completedTasks) : null,
                };

                dispatch(PublicStore.actionCreators.sendFirstResponse(responseData, { callback }));
            }
        } else if (callback) {
            callback();
        }
    };

    React.useEffect(() => {
        if (responsesForCurrentTask && changeId && Auth.isUserAuthenticated()) {
            if (saveTimer.current) {
                clearTimeout(saveTimer.current);
            }
            saveTimer.current = setTimeout(() => {
                saveResponses(responsesForCurrentTask);
            }, AUTOSAVE_TIMEOUT_INTERVAL);
        }
    }, [changeId, responsesForCurrentTask, saveResponses, task.id]);

    const getLinkUrl = link => getPublicProgramContentLink(link, program);

    const getProgramItem = id => getItemFromProgram(program, id);

    return (
        <>
            <Loading loading={!!showSavingId} />

            <TaskContent
                ref={forwardedRef}
                className={classnames(classes.root)}
                LinkElementProps={{
                    getLinkUrl,
                }}
                task={task}
                taskResponses={responsesForCurrentTask}
                getProgramItem={getProgramItem}
                getSpecificTaskResponses={getSpecificTaskResponses(allTaskResponses, responsesForCurrentTask)}
                translations={getContentTranslations(translate)}
                onResponse={updateResponse}
                onSaveChanges={saveChanges}
            />
        </>
    );
};

PublicTaskContent.propTypes = {
    forwardedRef: PropTypes.object,
    task: PropTypes.object.isRequired,
    program: PropTypes.object.isRequired,
    moduleId: PropTypes.string.isRequired,
};

PublicTaskContent.defaultProps = {
    forwardedRef: null,
};

const WithRef = React.forwardRef((props, ref) => <PublicTaskContent {...props} forwardedRef={ref} />);
export default WithRef;
